/* import __COLOCATED_TEMPLATE__ from './phone-office-hours.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type { Schedule } from 'embercom/services/fin-voice-config-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumberId?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateOfficeHours: (phoneNumberId: string, schedule: string | undefined) => Promise<void>;
    addAndSaveNewOfficeHoursSchedule: (phoneNumberId: string, schedule: any) => Promise<void>;
  };
}

export default class PhoneOfficeHours extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare router: RouterService;

  @tracked officeHoursScheduleComparison: 'in' | 'nin' | 'anytime' = 'anytime';
  @tracked selectedOfficeHoursSchedule?: string;

  @tracked newSchedule: Schedule | null = null;
  @tracked showModal = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.populateInitialOfficeHoursSchedules();
  }

  get officeHoursSchedules() {
    return this.finVoiceConfigService.officeHoursSchedules;
  }

  get isSaving() {
    return Boolean(this.numberConfiguration?.isSaving);
  }

  get savingDisabled() {
    return !this.args.phoneNumberId || this.isSaving;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedOfficeHoursScheduleComparison() {
    return this.numberConfiguration?.predicates?.officeHoursComparison || 'anytime';
  }

  get savedOfficeHoursSchedule() {
    return this.numberConfiguration?.predicates?.officeHoursSchedule;
  }

  get activeOfficeHoursScheduleDescription() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.description;
  }

  get activeOfficeHoursScheduleLabel() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.text;
  }

  get contentPageUrl(): string {
    return this.router.urlFor('apps.app.automation.fin-ai-agent.setup', {
      queryParams: {
        activeTab: 'add-content',
      },
    });
  }

  get shouldShowPaywall() {
    return this.finVoiceConfigService.shouldShowPaywall;
  }

  get officeHoursDaysOfWeek() {
    let parts = this.activeOfficeHoursScheduleDescription?.split('•');
    let scheduleText = parts?.[0]?.trim();
    let daysMatch = scheduleText?.match(/(.+?)\s\d/);
    return daysMatch?.[1]?.trim() || '';
  }

  get officeHoursTimeRange() {
    let parts = this.activeOfficeHoursScheduleDescription?.split('•');
    let scheduleText = parts?.[0]?.trim();
    let timeMatch = scheduleText?.match(/\d.+$/);
    return timeMatch?.[0]?.trim() || '';
  }

  get officeHoursLocation() {
    let parts = this.activeOfficeHoursScheduleDescription?.split('•');
    return parts?.[1]?.trim();
  }

  get hasChanges() {
    return this.selectedOfficeHoursSchedule !== this.savedOfficeHoursSchedule;
  }

  get errorMessage() {
    if (!this.args.phoneNumberId) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.no-phone-numbers',
      );
    }

    if (!this.hasChanges) {
      return this.intl.t('operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.no-changes');
    }

    return undefined;
  }

  @action onSelectOfficeHoursSchedule(value: string) {
    this.selectedOfficeHoursSchedule = value;
  }

  @action
  populateInitialOfficeHoursSchedules() {
    this.selectedOfficeHoursSchedule =
      this.savedOfficeHoursSchedule || this.finVoiceConfigService.getDefaultOfficeHoursSchedule();

    this.officeHoursScheduleComparison = this.savedOfficeHoursScheduleComparison;
  }

  @action
  openNewScheduleModal() {
    this.newSchedule = this.store.createRecord('office-hours-schedule');
    this.showModal = true;
  }

  @action
  onModalSave(schedule: Schedule) {
    if (!this.args.phoneNumberId) {
      return;
    }

    this.selectedOfficeHoursSchedule = schedule.id;
    this.newSchedule = null;

    this.args.addAndSaveNewOfficeHoursSchedule(this.args.phoneNumberId, schedule);
  }

  @action
  async updateOfficeHoursSchedule(value: string) {
    if (this.savingDisabled || !this.args.phoneNumberId) {
      return;
    }

    this.selectedOfficeHoursSchedule = value;

    await this.args.updateOfficeHours(this.args.phoneNumberId, this.selectedOfficeHoursSchedule);
  }

  @action
  resetOfficeHoursSchedule() {
    this.selectedOfficeHoursSchedule = this.savedOfficeHoursSchedule;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneOfficeHours': typeof PhoneOfficeHours;
  }
}
